import { TPSScores, BigFiveDetail } from '../types/tps.types';
import { calculateConfidence } from './utils';
import { getWeight } from './weightLoader';
import type { DimensionConfig, FacetConfig } from '../types/mapping.types';

/**
 * Expected score range for TPS traits
 * Used for validation and normalization
 */
const EXPECTED_SCORE_MIN = 1;
const EXPECTED_SCORE_MAX = 10;
const NEUTRAL_SCORE = 5;

/**
 * Validates if a score is within expected range
 */
function isValidScore(score: number): boolean {
  return typeof score === 'number' && 
         !isNaN(score) && 
         score >= EXPECTED_SCORE_MIN && 
         score <= EXPECTED_SCORE_MAX;
}

/**
 * Gets trait score with validation and fallback
 * Returns neutral score (5) for missing or invalid traits
 */
function getTraitScore(scores: TPSScores, trait: string): { value: number; isFallback: boolean } {
  const score = scores[trait];
  
  if (score === undefined) {
    return { value: NEUTRAL_SCORE, isFallback: true };
  }
  
  if (!isValidScore(score)) {
    return { value: NEUTRAL_SCORE, isFallback: true };
  }
  
  return { value: score, isFallback: false };
}

// HEXACO Big Six personality dimensions
// Recalibrated mappings to better align with expected personality correlations
// Key changes:
// - Openness: Added Analytical as HIGH indicator for inquisitiveness (intellectual curiosity)
// - Openness: Removed Structured from LOW indicators (structure ≠ closed-mindedness)
// - Conscientiousness: Decoupled Analytical (cognitive style, not orderliness)
// - Agreeableness: Balanced to not over-penalize Independent thinkers
export const TPS_TO_HEXACO_FACETS = {
  'Honesty-Humility': {
    facets: {
      sincerity: {
        high: ['Lawful', 'Self-Principled', 'Diplomatic', 'Direct'],
        low: ['Self-Indulgent', 'Pragmatic', 'Mixed Communication', 'Varied']
      },
      fairness: {
        high: ['Lawful', 'Social', 'Communal Navigate', 'Self-Principled'], // TPS-EXEMPT: Social=processing.reality.social
        low: ['Self-Indulgent', 'Independent', 'Pragmatic', 'Extrinsic'] // TPS-EXEMPT: Independent=interpersonal.navigate.independent
      },
      greedAvoidance: {
        high: ['Intrinsic', 'Self-Principled', 'Passive', 'Communal Navigate'],
        low: ['Extrinsic', 'Self-Indulgent', 'Assertive', 'Independent Navigate']
      },
      modesty: {
        high: ['Passive', 'Intrinsic', 'Diplomatic', 'Mixed Communication'],
        low: ['Assertive', 'Extrinsic', 'Direct', 'Self-Indulgent']
      }
    }
  },

  Emotionality: {
    facets: {
      fearfulness: {
        high: ['Turbulent', 'Pessimistic', 'Ambivalent', 'Responsive Regulation'],
        low: ['Stoic', 'Optimistic', 'Self-Mastery', 'Assertive']
      },
      anxiety: {
        high: ['Turbulent', 'Pessimistic', 'Self-Aware', 'Ambivalent'],
        low: ['Stoic', 'Optimistic', 'Self-Mastery', 'Dynamic']
      },
      dependence: {
        high: ['Passive', 'Social', 'Communal Navigate', 'Responsive'], // TPS-EXEMPT: Social=processing.reality.social
        low: ['Independent', 'Assertive', 'Self-Mastery', 'Direct'] // TPS-EXEMPT: Independent=interpersonal.navigate.independent
      },
      sentimentality: {
        high: ['Responsive', 'Social', 'Turbulent', 'Self-Aware'], // TPS-EXEMPT: Social=processing.reality.social
        low: ['Stoic', 'Independent Navigate', 'Physical']
      }
    }
  },
  
  Extraversion: {
    facets: {
      socialSelfEsteem: {
        high: ['Assertive', 'Optimistic', 'Social', 'Self-Mastery'], // TPS-EXEMPT: Social=processing.reality.social
        low: ['Passive', 'Pessimistic', 'Independent Navigate', 'Turbulent']
      },
      socialBoldness: {
        high: ['Assertive', 'Direct', 'Dynamic', 'Optimistic'],
        low: ['Passive', 'Ambivalent', 'Pessimistic', 'Intrinsic']
      },
      sociability: {
        high: ['Social', 'Communal Navigate', 'Dynamic', 'Optimistic'], // TPS-EXEMPT: Social=processing.reality.social
        low: ['Independent Navigate', 'Intrinsic', 'Static', 'Passive']
      },
      liveliness: {
        high: ['Dynamic', 'Optimistic', 'Assertive', 'Extrinsic'],
        low: ['Static', 'Pessimistic', 'Passive', 'Intrinsic']
      }
    }
  },

  // Agreeableness: measures how someone treats people, not their pace or
  // communication polish.
  // - Diplomatic is a communication style (its opposite pole is Direct), so it
  //   no longer appears in every facet and never dominates the dimension.
  // - The real antagonism markers (Assertive, Direct, Independent Navigate,
  //   Pragmatic, Self-Indulgent) now count on the low side.
  // - Static was removed from patience: preferring stability is not tolerance.
  // - Turbulent is emotional volatility, so it only remains where impatience
  //   genuinely tracks it.
  Agreeableness: {
    facets: {
      forgiveness: {
        high: ['Diplomatic', 'Optimistic', 'Social', 'Stoic'], // TPS-EXEMPT: Social=processing.reality.social
        low: ['Assertive', 'Self-Indulgent', 'Pessimistic']
      },
      gentleness: {
        high: ['Responsive', 'Social', 'Passive'], // TPS-EXEMPT: Social=processing.reality.social
        low: ['Physical', 'Assertive', 'Direct', 'Self-Indulgent']
      },
      flexibility: {
        high: ['Varied', 'Diplomatic', 'Ambivalent'],
        low: ['Self-Principled', 'Structured', 'Independent Navigate'] // TPS-EXEMPT: Independent Navigate=interpersonal.navigate.independent
      },
      patience: {
        high: ['Stoic', 'Optimistic'],
        low: ['Turbulent', 'Assertive', 'Self-Indulgent', 'Pragmatic']
      }
    }
  },


  // Conscientiousness: Decoupled from Analytical trait
  // Analytical is a cognitive style, not a measure of orderliness or diligence
  // Focus on actual indicators: Structured, Lawful, Self-Mastery
  Conscientiousness: {
    facets: {
      organization: {
        high: ['Structured', 'Lawful', 'Self-Mastery'],
        low: ['Ambivalent', 'Self-Indulgent', 'Varied']
      },
      diligence: {
        high: ['Self-Mastery', 'Structured', 'Lawful', 'Assertive'],
        low: ['Self-Indulgent', 'Passive', 'Ambivalent']
      },
      perfectionism: {
        high: ['Self-Mastery', 'Structured', 'Self-Principled'],
        low: ['Self-Indulgent', 'Ambivalent', 'Varied']
      },
      prudence: {
        high: ['Pessimistic', 'Structured', 'Lawful', 'Static'],
        low: ['Optimistic', 'Dynamic', 'Self-Indulgent', 'Varied']
      }
    }
  },

  // Openness to Experience: Recalibrated for intellectual types
  // Added Analytical as HIGH indicator for inquisitiveness (intellectual curiosity)
  // Removed Structured from LOW indicators - structure doesn't preclude openness
  // INTPs and other analytical types should score appropriately high
  'Openness to Experience': {
    facets: {
      aestheticAppreciation: {
        high: ['Intuitive', 'Self-Principled', 'Responsive', 'Universal'],
        low: ['Pragmatic', 'Physical', 'Stoic']
      },
      // Inquisitiveness: Intellectual curiosity - Analytical types score HIGH here
      inquisitiveness: {
        high: ['Analytical', 'Universal', 'Intuitive', 'Self-Aware', 'Varied'],
        low: ['Static', 'Self-Indulgent']
      },
      // Creativity: Added Analytical (creative problem-solving), removed Structured penalty
      creativity: {
        high: ['Intuitive', 'Universal', 'Dynamic', 'Varied', 'Analytical'],
        low: ['Physical', 'Static', 'Self-Indulgent']
      },
      // Unconventionality: Independent thinking indicates openness
      unconventionality: {
        high: ['Self-Principled', 'Independent', 'Varied', 'Dynamic', 'Analytical'], // TPS-EXEMPT: Independent=interpersonal.navigate.independent
        low: ['Lawful', 'Static', 'Passive']
      }
    }
  }
};

// Facet weights for more accurate dimension scoring
// Inquisitiveness and creativity weighted higher for intellectual openness
const FACET_WEIGHTS: Record<string, Record<string, number>> = {
  'Openness to Experience': {
    aestheticAppreciation: 0.8,
    inquisitiveness: 1.3,  // Weighted higher for intellectual curiosity
    creativity: 1.2,       // Weighted higher
    unconventionality: 0.8
  },
  'Conscientiousness': {
    organization: 1.0,
    diligence: 1.2,        // Core to conscientiousness
    perfectionism: 0.9,
    prudence: 0.9
  },
  'Agreeableness': {
    forgiveness: 1.2,      // Compassion core
    gentleness: 1.3,       // Compassion core
    flexibility: 0.7,      // Closest to cognitive style, weakest evidence
    patience: 1.0
  },

  'Extraversion': {
    socialSelfEsteem: 1.0,
    socialBoldness: 1.0,
    sociability: 1.2,      // Core to extraversion
    liveliness: 0.9
  },
  'Emotionality': {
    fearfulness: 1.0,
    anxiety: 1.0,
    dependence: 0.9,
    sentimentality: 1.0
  },
  'Honesty-Humility': {
    sincerity: 1.0,
    fairness: 1.0,
    greedAvoidance: 1.0,
    modesty: 1.0
  }
};

/**
 * Opposite poles of the same TPS triad/pair. A respondent who endorses both
 * ends strongly (e.g. Diplomatic 7.8 *and* Direct 10) has produced a
 * self-report contradiction, so neither side is evidence for anything. Rather
 * than averaging the contradiction into a facet, both traits are suppressed and
 * the dimension is marked lower-confidence.
 */
// Only genuine opposite POLES of the same TPS triad belong here. Middle
// positions (Diplomatic, Ambivalent, Mixed Communication, ...) are balance
// points, not opposites, so endorsing them alongside a pole is consistent.
const OPPOSITE_POLES: Array<[string, string]> = [
  ['Structured', 'Independent'], // TPS-EXEMPT: pole of the Structure triad (paired with Structured), not the Navigate one
  ['Passive', 'Assertive'],
  ['Static', 'Dynamic'],
  ['Optimistic', 'Pessimistic'],
  ['Intrinsic', 'Extrinsic'],
  ['Direct', 'Passive Communication'],
  ['Communal Navigate', 'Independent Navigate'],
];

const CONTRADICTION_THRESHOLD = 7;

/** Traits to ignore because the respondent endorsed both poles of their pair. */
function findContradictedTraits(scores: TPSScores): Set<string> {
  const suppressed = new Set<string>();
  OPPOSITE_POLES.forEach(([a, b]) => {
    const sa = scores[a];
    const sb = scores[b];
    if (
      typeof sa === 'number' && typeof sb === 'number' &&
      sa >= CONTRADICTION_THRESHOLD && sb >= CONTRADICTION_THRESHOLD
    ) {
      suppressed.add(a);
      suppressed.add(b);
    }
  });
  return suppressed;
}

/**
 * Calculates a facet score based on high and low trait indicators
 * Uses a normalized weighted approach to produce full 1-10 range
 *
 * Old formula: (highAvg + (10 - lowAvg)) / 2 = compressed 3-7 range
 * New formula: Uses normalized 0-1 scaling with weighted combination
 */
function calculateFacetScore(
  scores: TPSScores,
  facetConfig: { high: string[]; low: string[] },
  suppressed: Set<string> = new Set(),
): { score: number; fallbackCount: number; totalTraits: number; suppressedCount: number } {
  let fallbackCount = 0;
  let suppressedCount = 0;
  const totalTraits = facetConfig.high.length + facetConfig.low.length;

  const usable = (traits: string[]) => {
    const kept = traits.filter((t) => !suppressed.has(t));
    suppressedCount += traits.length - kept.length;
    // Every indicator on this side contradicted itself: fall back to neutral
    // rather than letting an empty list produce NaN.
    return kept.length > 0 ? kept : null;
  };

  const average = (traits: string[] | null) => {
    if (!traits) return NEUTRAL_SCORE;
    return traits.reduce((sum, trait) => {
      const result = getTraitScore(scores, trait);
      if (result.isFallback) fallbackCount++;
      return sum + result.value;
    }, 0) / traits.length;
  };

  // Higher = more of this facet
  const highScore = average(usable(facetConfig.high));
  // Higher = less of this facet
  const lowScore = average(usable(facetConfig.low));

  // Normalize each score to 0-1 range
  const highNormalized = (highScore - EXPECTED_SCORE_MIN) / (EXPECTED_SCORE_MAX - EXPECTED_SCORE_MIN);
  const lowNormalized = (lowScore - EXPECTED_SCORE_MIN) / (EXPECTED_SCORE_MAX - EXPECTED_SCORE_MIN);
  
  // Symmetric high/low differential. The previous 0.65/0.35 split biased every
  // facet toward the centre because the two halves no longer cancelled out.
  const combinedScore = (highNormalized * 0.5) + ((1 - lowNormalized) * 0.5);
  
  // Scale back to 1-10 range
  const facetScore = EXPECTED_SCORE_MIN + (combinedScore * (EXPECTED_SCORE_MAX - EXPECTED_SCORE_MIN));
  
  return {
    score: Math.max(EXPECTED_SCORE_MIN, Math.min(EXPECTED_SCORE_MAX, facetScore)),
    fallbackCount,
    totalTraits,
    suppressedCount,
  };
}

function calculateDimensionScore(
  scores: TPSScores,
  dimensionConfig: DimensionConfig,
  dimensionName: string,
  suppressed: Set<string> = new Set(),
) {
  const facetScores: Record<string, number> = {};
  let weightedTotal = 0;
  let totalWeight = 0;
  let dimensionFallbackCount = 0;
  let dimensionTotalTraits = 0;
  let dimensionSuppressedCount = 0;
  
  const defaultWeights = FACET_WEIGHTS[dimensionName] || {};
  
  Object.entries(dimensionConfig.facets).forEach(([facetName, facetConfig]: [string, FacetConfig]) => {
    const facetResult = calculateFacetScore(scores, facetConfig, suppressed);
    facetScores[facetName] = facetResult.score;
    dimensionFallbackCount += facetResult.fallbackCount;
    dimensionTotalTraits += facetResult.totalTraits;
    dimensionSuppressedCount += facetResult.suppressedCount;
    
    // Get facet weight with override support
    const defaultWeight = defaultWeights[facetName] || 1.0;
    const weight = getWeight('Big Five', dimensionName, facetName, defaultWeight);
    
    weightedTotal += facetResult.score * weight;
    totalWeight += weight;
  });
  
  const dimensionScore = weightedTotal / totalWeight;
  const fallbackRatio = dimensionTotalTraits > 0 ? dimensionFallbackCount / dimensionTotalTraits : 0;
  
  return {
    score: dimensionScore,
    facets: facetScores,
    fallbackCount: dimensionFallbackCount,
    totalTraits: dimensionTotalTraits,
    suppressedCount: dimensionSuppressedCount,
    confidence: fallbackRatio > 0.5 || dimensionSuppressedCount > 0 ? 'low' as const : 'normal' as const,
  };
}


/**
 * Expands scores from the compressed observed range (~4.5-7.5) to a fuller
 * 2-9 range, correcting for the averaging effect in facet calculations.
 *
 * Why 2.5: raw item responses are averaged three times on the way to a
 * dimension score (items -> traits -> facets -> dimension). Each averaging
 * pass pulls values toward the 5.5 midpoint, so the observed spread collapses
 * to roughly 40% of the usable 1-10 scale. Multiplying the deviation from
 * centre by 1 / 0.4 = 2.5 restores the original spread without shifting the
 * mean. Changing this constant re-scales every Big Five percentile band, so
 * it must stay in sync with the 9-band percentile table.
 */
function expandScore(score: number): number {
  const center = 5.5;
  const scaleFactor = BIG_FIVE_SCALE_FACTOR;
  
  const expanded = center + (score - center) * scaleFactor;
  // Clamp to 1-10 and round to 1 decimal
  return Math.max(1, Math.min(10, Math.round(expanded * 10) / 10));
}

/** Inverse of the ~0.4 compression caused by triple-averaging. See `expandScore`. */
export const BIG_FIVE_SCALE_FACTOR = 2.2;

/** A single HEXACO/Big Five dimension after expansion. */
export interface BigFiveDimensionScore {
  score: number;
  facets: Record<string, number>;
}

export function calculateBigFiveEnhanced(scores: TPSScores): BigFiveDetail {
  const dimensions: Record<string, BigFiveDimensionScore> = {};
  let totalConfidence = 0;
  const suppressed = findContradictedTraits(scores);
  let contradictionPenalty = 0;
  
  Object.entries(TPS_TO_HEXACO_FACETS).forEach(([dimensionName, dimensionConfig]) => {
    const result = calculateDimensionScore(scores, dimensionConfig, dimensionName, suppressed);
    if (result.suppressedCount > 0) contradictionPenalty += 0.1;

    
    // Expand the dimension score to use fuller range
    const expandedScore = expandScore(result.score);
    
    // Also expand facet scores for consistency
    const expandedFacets: Record<string, number> = {};
    for (const [facetName, facetScore] of Object.entries(result.facets)) {
      expandedFacets[facetName] = expandScore(facetScore as number);
    }
    
    dimensions[dimensionName] = {
      score: expandedScore,
      facets: expandedFacets
    };
    
    // Calculate confidence based on facet consistency (0..1 unit scale)
    const facetValues = Object.values(expandedFacets) as number[];
    const variance = facetValues.reduce((sum, val) => sum + Math.pow(val - expandedScore, 2), 0) / facetValues.length;
    const consistency = Math.max(0, 1 - variance * 0.1); // Lower variance = higher confidence
    totalConfidence += consistency;
  });
  
  const overallConfidence = Math.max(
    0,
    totalConfidence / Object.keys(TPS_TO_HEXACO_FACETS).length - contradictionPenalty,
  );

  
  return {
    // The loop builds the dimension map dynamically from TPS_TO_HEXACO_FACETS,
    // whose keys are exactly the six BigFiveDetail dimensions.
    dimensions: dimensions as BigFiveDetail['dimensions'],
    confidence: overallConfidence
  };
}